<template>
  <div
    class="provider-card text-sm md:text-base text-black-high-emphasis"
  >
    <p class="provider-card__network text-base text-gray-darkest font-medium md:font-bold">
      {{ content.networkText }}
    </p>
    <p class="provider-card__savings-label font-medium leading-normal">
      {{ content.savingsText }}
    </p>
    <p class="provider-card__savings-value font-medium leading-normal" data-testid="savings-value">
      ${{ savingsValue }}
    </p>
    <div
      class="provider-card__provider flex items-start gap-2 min-w-0 text-base font-bold text-gray-darkest"
      :style="style"
    >
      <div
        v-if="content.isPlusProvider"
        class="provider-card__icon"
        v-dompurify-html:svg="iconSource"
      />
      {{ content.providerText }}
    </div>
    <p class="provider-card__member-label tracking-0.5px font-bold leading-normal">
      {{ content.memberCostText }}
    </p>
    <p
      class="provider-card__member-value font-bold leading-normal text-gray-darkest"
      :style="style"
      data-testid="member-cost-value"
    >
      ${{ memberCostValue }}
    </p>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import ProviderCard from '@/model/ProviderCard'
import useRemoteIcon from '@/hooks/useRemoteIcon'
import { useFlow } from '@/store'

export default defineComponent({
  props: {
    content: {
      type: Object as () => ProviderCard,
      required: true,
      validator: (value: ProviderCard) => value instanceof ProviderCard
    }
  },
  setup (props) {
    const { pricingSummary: pricing } = useFlow()
    const memberCostValue = props.content.isPlusProvider ? pricing.totalMoop : pricing.totalMoopNonPlus
    const savingsValue = props.content.isPlusProvider ? pricing.saving : pricing.savingNonPlus
    const iconSource = props.content.plusProviderIcon?.isIcon && props.content.plusProviderIcon.source
      ? useRemoteIcon(props.content.plusProviderIcon.source)
      : null
    const style = computed(() => {
      const isPlusProvider = props.content.isPlusProvider
      return isPlusProvider ? { color: props.content.plusColor } : {}
    })

    return {
      memberCostValue,
      savingsValue,
      iconSource,
      style
    }
  }
})
</script>

<style>
.provider-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  grid-gap: 6px 16px;

  &__network,
  &__provider {
    grid-column: 1 / -1;
  }

  &__provider {
    overflow-wrap: anywhere;
  }

  &__savings-label,
  &__member-label {
    grid-column: 1;
  }

  &__savings-value,
  &__member-value {
    grid-column: 2;
    justify-self: end;
  }

  &__icon svg {
    @apply h-3 w-5 fill-current flex-shrink-0
  }
}

@screen md {
  .provider-card {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) max-content;
    grid-gap: 6px 1.5rem;

    &__network,
    &__provider {
      grid-column: 1;
    }

    &__savings-label,
    &__member-label {
      grid-column: 2;
    }

    &__savings-value,
    &__member-value {
      grid-column: 3;
      justify-self: stretch;
      text-align: right;
    }

    &__icon svg {
      @apply h-4 w-6;
    }
  }

  .price-comparison__comparison {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) max-content;
    grid-gap: 6px 1.5rem;
    align-items: start;

    .provider-card {
      display: contents;
    }
  }

  .price-comparison__divider {
    grid-column: 1 / -1;
    width: 100%;
  }
}
</style>
